let getToken: () => string | null = () => null

let onUnauthorized: () => void = () => {}

export function setAuthTokenGetter(fn: () => string | null): void {
  getToken = fn
}

export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn
}

export class HttpError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = "HttpError"
    this.status = status
  }
}

// crypto.randomUUID requires a secure context. localhost and HTTPS qualify, but
// the dev server is reachable over plain HTTP on the LAN (host: true), where it is
// undefined — fall back to a UUIDv4 built from getRandomValues so the id stays a
// valid UUID the backend will adopt rather than regenerate.
function requestId(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

async function request<T>(url: string, options: RequestInit): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Request-Id": requestId(),
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(url, { ...options, headers })

  if (!response.ok) {
    if (response.status === 401 && token && token === getToken()) {
      onUnauthorized()
    }
    let message = `${response.status} ${response.statusText}`
    try {
      const body = await response.json()
      if (body && typeof body.detail === "string") {
        message = body.detail
      } else if (body && typeof body.error === "string") {
        message = body.error
      }
    } catch {
      // no JSON body; keep the status-text fallback
    }
    throw new HttpError(response.status, message)
  }

  // 204 and other empty-body success responses have nothing to parse;
  // response.json() would reject on the empty stream.
  if (response.status === 204 || response.headers?.get("Content-Length") === "0") {
    return undefined as T
  }

  return response.json() as Promise<T>
}

export const httpClient = {
  get<T>(url: string): Promise<T> {
    return request<T>(url, { method: "GET" })
  },
  post<T>(url: string, body: unknown): Promise<T> {
    return request<T>(url, { method: "POST", body: JSON.stringify(body) })
  },
  delete<T = void>(url: string): Promise<T> {
    return request<T>(url, { method: "DELETE" })
  },
}
